'use client';

import { c as _c } from "react/compiler-runtime";
import './drawer.scss';
import type { PropsWithChildren, ReactNode, Ref } from 'react';
import { Suspense } from 'react';
import { VisuallyHidden } from 'react-aria';
import { Sizes, Text, Variants } from '../../atoms/text/text.tsx';
import { cn } from '../../utils/helpers.ts';
import { ModalCloseButton, ModalContent, type ModalContentProps, ModalTitle } from '../modal/modal.tsx';
import type { ModalControlledTriggerProps, ModalProps } from '../modal/modal.tsx';
export { Modal as Drawer } from '../modal/modal.tsx';
export { ModalClose as DrawerClose } from '../modal/modal.tsx';
export { ModalControlledTrigger as DrawerControlledTrigger } from '../modal/modal.tsx';
export { ModalTrigger as DrawerTrigger } from '../modal/modal.tsx';
export type DrawerProps = ModalProps;
export type DrawerControlledTriggerProps = ModalControlledTriggerProps;
export type DrawerContentProps = ModalContentProps & {
  ref?: Ref<HTMLDivElement>;
  subtitle?: ReactNode;
  title?: string;
};
interface DrawerContainerProps extends PropsWithChildren {
  className?: string;
}
const DrawerContainer: React.FC<DrawerContainerProps> = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("Drawer__container p-comfy-sm md:px-comfy-xl", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const DrawerHeader: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <DrawerContainer className="Drawer__header">{children}</DrawerContainer>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const DrawerBody: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="Drawer__body flex-1 overflow-y-auto">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const DrawerFooter: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <DrawerContainer className="Drawer__footer">{children}</DrawerContainer>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const DrawerContent: React.FC<DrawerContentProps> = t0 => {
  const $ = _c(32);
  let children;
  let className;
  let props;
  let ref;
  let style;
  let subtitle;
  let title;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ref,
      style,
      subtitle,
      title,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
    $[5] = style;
    $[6] = subtitle;
    $[7] = title;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
    style = $[5];
    subtitle = $[6];
    title = $[7];
  }
  let t1;
  if ($[8] !== className) {
    t1 = cn("Drawer__content h-screen flex flex-col top-0 right-0", className);
    $[8] = className;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== style) {
    t2 = {
      ...style
    };
    $[10] = style;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== title) {
    t3 = title && <VisuallyHidden>{title}</VisuallyHidden>;
    $[12] = title;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ModalCloseButton className="Drawer__close absolute right-0 mr-snug-md mt-snug-md" name="Drawer" />;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== title) {
    t5 = title && <Text asChild={true} variant={Variants.Heading} size={Sizes.Small}><ModalTitle>{title}</ModalTitle></Text>;
    $[15] = title;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== subtitle) {
    t6 = subtitle && <Text variant={Variants.Label} size={Sizes.Medium} asChild={true}><p className="mt-snug-lg">{subtitle}</p></Text>;
    $[17] = subtitle;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <DrawerHeader><div className="Drawer__header-copy min-w-0">{t5}{t6}</div></DrawerHeader>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== children) {
    t8 = <Suspense fallback={null}>{children}</Suspense>;
    $[22] = children;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== props || $[25] !== ref || $[26] !== t1 || $[27] !== t2 || $[28] !== t3 || $[29] !== t7 || $[30] !== t8) {
    t9 = <ModalContent ref={ref} aria-describedby={undefined} className={t1} style={t2} {...props}>{t3}{t4}{t7}{t8}</ModalContent>;
    $[24] = props;
    $[25] = ref;
    $[26] = t1;
    $[27] = t2;
    $[28] = t3;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
};
export const DrawerActions: React.FC<PropsWithChildren> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex items-center justify-center gap-comfy-xs md:gap-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};